import {EdrtzRequestDialog} from "@/components/document-editor/EdrtzRequestDialog"
import {YesNoRadio} from "@/components/document-editor/YesNoRadio"
import {FormCard} from "@/components/FormCard"
import {Checkbox} from "@/components/ui/checkbox"
import {Field, FieldError, FieldGroup, FieldLabel, FieldRow,} from "@/components/ui/field"
import {Input} from "@/components/ui/input"
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group"
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue,} from "@/components/ui/select"
import {Separator} from "@/components/ui/separator"
import {Textarea} from "@/components/ui/textarea"
import {DatePicker} from "@/components/DatePicker"
import {
    cargoCapacityCategories,
    ecologyLevels,
    otkPeriodicityOptions,
    TAXI_PURPOSE,
    vehicleCategories,
    vehiclePurposes,
    type VehicleTabState,
} from "@/lib/documentEditor"

export function VehicleTab({
  value,
  errors,
  onChange,
  onCommit,
  categoryInvalid = false,
}: {
  value: VehicleTabState
  /** Per-field messages from shared's `editableDraftFields`, keyed the same as `value` — see DocumentEdit.tsx's `fieldErrors`. Only set for fields with a backend counterpart that a free-text input could actually get wrong (Select/RadioGroup-backed fields can't). */
  errors: Partial<Record<keyof VehicleTabState, string>>
  onChange: (patch: Partial<VehicleTabState>) => void
  /** Autosaves a patch immediately (select/checkbox/radio/date fields) — text fields autosave on blur instead. */
  onCommit: (patch: Partial<VehicleTabState>) => void
  /** document-editor.md: the selected category is outside the subject's own permit — marks the field invalid; the actual message lives in `FormChapterValidationAlert`. */
  categoryInvalid?: boolean
}) {
  function togglePurpose(purpose: string, checked: boolean) {
    const purposes = checked
      ? [...value.purposes, purpose]
      : value.purposes.filter((p) => p !== purpose)
    const patch: Partial<VehicleTabState> = { purposes }
    // Two-way sync with "Автомобіль експлуатується як таксі" — checking or
    // unchecking "Таксі" here sets the taxi YesNoRadio to match.
    if (purpose === TAXI_PURPOSE) patch.isTaxi = checked
    onChange(patch)
    onCommit(patch)
  }

  function setIsTaxi(checked: boolean) {
    const purposes = checked
      ? value.purposes.includes(TAXI_PURPOSE)
        ? value.purposes
        : [...value.purposes, TAXI_PURPOSE]
      : value.purposes.filter((p) => p !== TAXI_PURPOSE)
    const patch = {
      isTaxi: checked,
      purposes,
    }
    onChange(patch)
    onCommit(patch)
  }

  return (
    <div className="flex flex-col gap-4">
      <FormCard title="Ідентифікація транспортного засобу">
        <FieldGroup className="gap-4">
          <FieldRow>
            <Field data-invalid={Boolean(errors.plate)} length="4x" required>
              <FieldLabel htmlFor="vehicle-plate">Номерний знак</FieldLabel>
              <Input
                aria-invalid={Boolean(errors.plate)}
                id="vehicle-plate"
                onBlur={() => onCommit({ plate: value.plate })}
                onChange={(e) => onChange({ plate: e.target.value })}
                placeholder="АА0000BB"
                value={value.plate}
              />
              {errors.plate && <FieldError>{errors.plate}</FieldError>}
            </Field>
            <Field data-invalid={Boolean(errors.vin)} length="6x" required>
              <FieldLabel htmlFor="vehicle-vin">VIN-код</FieldLabel>
              <Input
                aria-invalid={Boolean(errors.vin)}
                id="vehicle-vin"
                onBlur={() => onCommit({ vin: value.vin })}
                onChange={(e) => onChange({ vin: e.target.value })}
                placeholder="17 символів"
                value={value.vin}
              />
              {errors.vin && <FieldError>{errors.vin}</FieldError>}
            </Field>
            <EdrtzRequestDialog plate={value.plate} vin={value.vin} />
          </FieldRow>
          <Separator />
          <FieldRow>
            <Field data-invalid={Boolean(errors.manufactureYear)} length="4x" required>
              <FieldLabel htmlFor="vehicle-year">Рік випуску ТЗ</FieldLabel>
              <Input
                aria-invalid={Boolean(errors.manufactureYear)}
                id="vehicle-year"
                max={new Date().getFullYear()}
                min={1886}
                onBlur={() => {
                  // `max` alone only marks the native input `:invalid` — it
                  // never stops the user from typing a later year, so clamp
                  // on commit instead of just flagging it.
                  const currentYear = new Date().getFullYear()
                  const parsedYear = Number(value.manufactureYear)
                  const clamped =
                    value.manufactureYear !== "" &&
                    Number.isFinite(parsedYear) &&
                    parsedYear > currentYear
                      ? String(currentYear)
                      : value.manufactureYear
                  if (clamped !== value.manufactureYear) {
                    onChange({ manufactureYear: clamped })
                  }
                  onCommit({ manufactureYear: clamped })
                }}
                onChange={(e) => onChange({ manufactureYear: e.target.value })}
                type="number"
                value={value.manufactureYear}
              />
              {errors.manufactureYear && (
                <FieldError>{errors.manufactureYear}</FieldError>
              )}
            </Field>
            <Field data-invalid={Boolean(errors.registrationDate)} length="4x">
              <FieldLabel htmlFor="vehicle-reg-date">
                Дата реєстрації
              </FieldLabel>
              <DatePicker
                disableFuture
                id="vehicle-reg-date"
                onChange={(next) => {
                  onChange({ registrationDate: next })
                  onCommit({ registrationDate: next })
                }}
                value={value.registrationDate}
              />
              {errors.registrationDate && (
                <FieldError>{errors.registrationDate}</FieldError>
              )}
            </Field>
          </FieldRow>
          <FieldRow>
            <Field data-invalid={Boolean(errors.brand)} required>
              <FieldLabel htmlFor="vehicle-brand">Марка</FieldLabel>
              <Input
                aria-invalid={Boolean(errors.brand)}
                id="vehicle-brand"
                onBlur={() => onCommit({ brand: value.brand })}
                onChange={(e) => onChange({ brand: e.target.value })}
                placeholder="Наприклад, Volkswagen"
                value={value.brand}
              />
              {errors.brand && <FieldError>{errors.brand}</FieldError>}
            </Field>
            <Field data-invalid={Boolean(errors.model)} required>
              <FieldLabel htmlFor="vehicle-model">Модель</FieldLabel>
              <Input
                aria-invalid={Boolean(errors.model)}
                id="vehicle-model"
                onBlur={() => onCommit({ model: value.model })}
                onChange={(e) => onChange({ model: e.target.value })}
                placeholder="Наприклад, Crafter"
                value={value.model}
              />
              {errors.model && <FieldError>{errors.model}</FieldError>}
            </Field>
            <Field length="6x">
              <FieldLabel htmlFor="vehicle-print-name">
                Марка, модель для друку
              </FieldLabel>
              <Input
                id="vehicle-print-name"
                onChange={(e) => onChange({ printName: e.target.value })}
                placeholder="Формується автоматично з марки та моделі"
                value={value.printName}
              />
            </Field>
          </FieldRow>
          <FieldRow>
            <Field data-invalid={categoryInvalid} length="3x">
              <FieldLabel htmlFor="vehicle-category">Категорія</FieldLabel>
              <Select
                onValueChange={(next) => {
                  onChange({ category: next })
                  onCommit({ category: next })
                }}
                value={value.category}
              >
                <SelectTrigger aria-invalid={categoryInvalid} id="vehicle-category" length="full-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {vehicleCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field data-invalid={Boolean(errors.vehicleType)}>
              <FieldLabel htmlFor="vehicle-type">Тип ТЗ</FieldLabel>
              <Input
                aria-invalid={Boolean(errors.vehicleType)}
                id="vehicle-type"
                onBlur={() => onCommit({ vehicleType: value.vehicleType })}
                onChange={(e) => onChange({ vehicleType: e.target.value })}
                placeholder="Наприклад, Легковий"
                value={value.vehicleType}
              />
              {errors.vehicleType && <FieldError>{errors.vehicleType}</FieldError>}
            </Field>
            <Field length="6x">
              <FieldLabel htmlFor="vehicle-ecology">
                Екологічний рівень
              </FieldLabel>
              <Select
                onValueChange={(next) => {
                  onChange({ ecologyLevel: next })
                  onCommit({ ecologyLevel: next })
                }}
                value={value.ecologyLevel}
              >
                <SelectTrigger id="vehicle-ecology" length="full-size">
                  <SelectValue placeholder="Оберіть рівень" />
                </SelectTrigger>
                <SelectContent>
                  {ecologyLevels.map((level) => (
                    <SelectItem key={level} value={level}>
                      {level}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </FieldRow>
          <FieldRow>
            <Field data-invalid={Boolean(errors.bodyNumber)}>
              <FieldLabel htmlFor="vehicle-body">Номер кузова</FieldLabel>
              <Input
                aria-invalid={Boolean(errors.bodyNumber)}
                id="vehicle-body"
                onBlur={() => onCommit({ bodyNumber: value.bodyNumber })}
                onChange={(e) => onChange({ bodyNumber: e.target.value })}
                placeholder="за наявності"
                value={value.bodyNumber}
              />
              {errors.bodyNumber && <FieldError>{errors.bodyNumber}</FieldError>}
            </Field>
            <Field data-invalid={Boolean(errors.chassisNumber)}>
              <FieldLabel htmlFor="vehicle-chassis">
                Номер шасі / рами
              </FieldLabel>
              <Input
                aria-invalid={Boolean(errors.chassisNumber)}
                id="vehicle-chassis"
                onBlur={() => onCommit({ chassisNumber: value.chassisNumber })}
                onChange={(e) => onChange({ chassisNumber: e.target.value })}
                placeholder="за наявності"
                value={value.chassisNumber}
              />
              {errors.chassisNumber && (
                <FieldError>{errors.chassisNumber}</FieldError>
              )}
            </Field>
          </FieldRow>
          {cargoCapacityCategories.has(
            value.category as (typeof vehicleCategories)[number]
          ) && (
            <FieldRow>
              <Field>
                <FieldLabel htmlFor="vehicle-cargo">
                  Вантажність, т.
                </FieldLabel>
                <Input
                  id="vehicle-cargo"
                  onChange={(e) => onChange({ cargoCapacity: e.target.value })}
                  placeholder="0.00"
                  type="number"
                  value={value.cargoCapacity}
                />
              </Field>
            </FieldRow>
          )}
        </FieldGroup>
      </FormCard>

      <FormCard title="Показник одометра">
        <FieldGroup className="gap-4">
          <FieldRow>
            <Field data-invalid={Boolean(errors.currentOdometer)} length="4x" required>
              <FieldLabel htmlFor="vehicle-odometer-current">
                Поточного ОТК, км
              </FieldLabel>
              <Input
                aria-invalid={Boolean(errors.currentOdometer)}
                id="vehicle-odometer-current"
                min={0}
                onBlur={() =>
                  onCommit({ currentOdometer: value.currentOdometer })
                }
                onChange={(e) => onChange({ currentOdometer: e.target.value })}
                placeholder="км"
                type="number"
                value={value.currentOdometer}
              />
              {errors.currentOdometer && (
                <FieldError>{errors.currentOdometer}</FieldError>
              )}
            </Field>
            <Field data-invalid={Boolean(errors.previousOdometer)} length="4x">
              <FieldLabel htmlFor="vehicle-odometer-previous">
                Попереднього ОТК, км
              </FieldLabel>
              <Input
                aria-invalid={Boolean(errors.previousOdometer)}
                id="vehicle-odometer-previous"
                min={0}
                onBlur={() =>
                  onCommit({ previousOdometer: value.previousOdometer })
                }
                onChange={(e) =>
                  onChange({ previousOdometer: e.target.value })
                }
                type="number"
                value={value.previousOdometer}
              />
              {errors.previousOdometer && (
                <FieldError>{errors.previousOdometer}</FieldError>
              )}
            </Field>
          </FieldRow>
        </FieldGroup>
      </FormCard>

      <FormCard title="Технічний контроль">
        <FieldGroup className="gap-4">
          <Field>
            <FieldLabel>
              Даний ТЗ підлягає обов'язковому технічному контролю
            </FieldLabel>
            <YesNoRadio
              id="vehicle-subject-to-inspection"
              onChange={(next) => onChange({ subjectToInspection: next })}
              value={value.subjectToInspection}
            />
          </Field>
          {value.subjectToInspection !== false && (
            <>
              <Field>
                <FieldLabel>Періодичність проходження ОТК</FieldLabel>
                <RadioGroup
                  className="flex gap-4"
                  onValueChange={(next) => onChange({ periodicity: next })}
                  value={value.periodicity}
                >
                  {otkPeriodicityOptions.map((option) => (
                    <label
                      className="flex items-center gap-2 text-sm"
                      htmlFor={`vehicle-periodicity-${option}`}
                      key={option}
                    >
                      <RadioGroupItem
                        id={`vehicle-periodicity-${option}`}
                        value={option}
                      />
                      {option}
                    </label>
                  ))}
                </RadioGroup>
              </Field>
              <Field length="4x">
                <FieldLabel htmlFor="vehicle-next-inspection">
                  Дата чергового проходження ОТК не пізніше
                </FieldLabel>
                <DatePicker
                  disablePast
                  id="vehicle-next-inspection"
                  onChange={(next) => onChange({ nextInspectionDate: next })}
                  value={value.nextInspectionDate}
                />
              </Field>
            </>
          )}
          <Field>
            <FieldLabel>
              Призначення ТЗ (у разі проведення додаткової перевірки)
            </FieldLabel>
            <div className="flex flex-col gap-2">
              {vehiclePurposes.map((purpose) => (
                <label
                  className="flex items-center gap-2 text-sm"
                  htmlFor={`vehicle-purpose-${purpose}`}
                  key={purpose}
                >
                  <Checkbox
                    checked={value.purposes.includes(purpose)}
                    id={`vehicle-purpose-${purpose}`}
                    onCheckedChange={(checked) =>
                      togglePurpose(purpose, checked === true)
                    }
                  />
                  {purpose}
                </label>
              ))}
            </div>
          </Field>
          <Field data-invalid={Boolean(errors.isTaxi)} required>
            <FieldLabel>Автомобіль експлуатується як таксі</FieldLabel>
            <YesNoRadio
              id="vehicle-is-taxi"
              onChange={setIsTaxi}
              value={value.isTaxi}
            />
            {errors.isTaxi && <FieldError>{errors.isTaxi}</FieldError>}
          </Field>
          <Field>
            <FieldLabel>Міжнародний технічний огляд проведено</FieldLabel>
            <YesNoRadio
              id="vehicle-international-inspection"
              onChange={(next) =>
                onChange({ internationalInspection: next })
              }
              value={value.internationalInspection}
            />
          </Field>
          <Field>
            <FieldLabel>ТЗ обладнано тахографом</FieldLabel>
            <YesNoRadio
              id="vehicle-has-tachograph"
              onChange={(next) => onChange({ hasTachograph: next })}
              value={value.hasTachograph}
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="vehicle-notes">
              Примітки (не передаються в ЄДРТЗ)
            </FieldLabel>
            <Textarea
              id="vehicle-notes"
              onChange={(e) => onChange({ notes: e.target.value })}
              placeholder="Внутрішні примітки для співробітників пункту ОТК"
              rows={3}
              value={value.notes}
            />
          </Field>
        </FieldGroup>
      </FormCard>
    </div>
  )
}
